import { serverTimestamp, type FieldValue } from 'firebase/firestore'
import {
  typedCollection,
  typedDoc,
  getCollection,
  updateDocument,
  deleteDocument,
  addDoc,
  orderBy,
} from '@/lib/firebase/firestore'
import type { EquipmentDocument } from '@/types'
import type { EquipmentFormValues } from '../types'

const equipmentCol = () => typedCollection<EquipmentDocument>('equipment')

type EquipmentCreatePayload = Omit<EquipmentDocument, 'id' | 'createdAt' | 'updatedAt'> & {
  createdAt: FieldValue
  updatedAt: FieldValue
}

export const adminEquipmentService = {
  async getAllEquipment(): Promise<Array<EquipmentDocument & { id: string }>> {
    return getCollection(equipmentCol(), orderBy('name', 'asc'))
  },

  async createEquipment(data: EquipmentFormValues): Promise<string> {
    const payload: EquipmentCreatePayload = {
      name: data.name,
      status: data.status,
      category: data.category,
      ...(data.manufacturer ? { manufacturer: data.manufacturer } : {}),
      ...(data.model ? { model: data.model } : {}),
      ...(data.serialNumber ? { serialNumber: data.serialNumber } : {}),
      ...(data.purchaseCost !== '' ? { purchaseCost: Number(data.purchaseCost) } : {}),
      ...(data.purchaseDate ? { purchaseDate: data.purchaseDate } : {}),
      ...(data.storageLocation ? { storageLocation: data.storageLocation } : {}),
      ...(data.notes ? { notes: data.notes } : {}),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    const ref = await addDoc(equipmentCol(), payload as unknown as EquipmentDocument)
    return ref.id
  },

  async updateEquipment(id: string, data: Partial<EquipmentFormValues>): Promise<void> {
    const ref = typedDoc<EquipmentDocument>('equipment', id)
    const updates: Record<string, unknown> = { updatedAt: serverTimestamp() }
    if (data.name !== undefined) updates.name = data.name
    if (data.status !== undefined) updates.status = data.status
    if (data.category !== undefined) updates.category = data.category
    if (data.manufacturer !== undefined) updates.manufacturer = data.manufacturer
    if (data.model !== undefined) updates.model = data.model
    if (data.serialNumber !== undefined) updates.serialNumber = data.serialNumber
    if (data.purchaseCost !== undefined) updates.purchaseCost = data.purchaseCost !== '' ? Number(data.purchaseCost) : null
    if (data.purchaseDate !== undefined) updates.purchaseDate = data.purchaseDate
    if (data.storageLocation !== undefined) updates.storageLocation = data.storageLocation
    if (data.notes !== undefined) updates.notes = data.notes
    await updateDocument(ref, updates as unknown as EquipmentDocument)
  },

  async deleteEquipment(id: string): Promise<void> {
    const ref = typedDoc<EquipmentDocument>('equipment', id)
    await deleteDocument(ref)
  },
}
